import type { QueryClient } from "@tanstack/react-query"
import {
  loadFrameworkSlice,
  type DeclarationFrameworkSlice,
} from "../../../../api/declarationsApi"
import type { StateContext, WorklistIssue } from "../studio/cellState"
import { deriveOpenWork } from "../studio/flowState"
import { flowInputsFromSlice } from "../studio/frameworkSliceInputs"
import {
  deriveFrameworkVerdict,
  type FrameworkDerivation,
  type InquiryState,
} from "./mapping/deriveFrameworkVerdict"
import { openPointDetail } from "./mapping/openPointDetail"
import {
  statementCardsByFramework,
  toUnitFacts,
  type StatementCard,
} from "./useStudioV2State"

/**
 * STEP C3-b3: A REGULATION THAT IS NOT OPEN, LOADED FOR A GESTURE.
 *
 * The paged review holds one regulation's cards. A gesture that acts on another
 * regulation (bulk conform, "link all", the request dialog, the open-point texts)
 * loads that regulation's slice at gesture time and derives exactly what flag off
 * derives for it: the same `deriveOpenWork` over the slice (as the open regulation is
 * derived), the same `statementCardsByFramework`, the same card decoration
 * (`openPointDetail` on its step-3 issues, the drafted-question mark, the supplier
 * pool facts). Its issues carry their article lists and its softened cells are its own
 * — never the open regulation's context.
 */
export interface FrameworkState {
  framework: string
  slice: DeclarationFrameworkSlice
  ctx: StateContext
  /** The regulation's open issues after overrides, with their article lists. */
  issues: WorklistIssue[]
  /** The regulation's cards in card order, decorated as on screen. */
  cards: StatementCard[]
  /** `fw:article` cells whose condition finding is softened (held back from bulk). */
  softenedCells: ReadonlySet<string>
  /** The regulation's R1-R4 derivation from these cards, exactly as flag off derives
   *  it (non-excluded cards, the cascade's inquiry state) — what a write gesture
   *  decides on, never the summary's, which can predate another session's edit. */
  derivation: FrameworkDerivation | null
}

export interface FrameworkStateOptions {
  /** The session's own marks, as the open regulation's derivation gets them. */
  pendingIssues?: Record<string, string[]>
  /** Statements carrying an unsent question (the card's drafted-question mark). */
  draftedStatements?: ReadonlySet<string>
  /** The R1 input of the derivation (sent/answered/drafted Rückfrage), as the page
   *  reads it for the open regulation. Absent: "none". */
  inquiryStateOf?: (framework: string) => InquiryState
}

/** One regulation's state from its slice — the pure half of `loadFrameworkState`. */
export function frameworkStateFromSlice(
  slice: DeclarationFrameworkSlice,
  options: FrameworkStateOptions = {},
): FrameworkState {
  const { ctx, worklist } = deriveOpenWork({
    ...flowInputsFromSlice(slice),
    pendingIssues: options.pendingIssues,
  })
  const cards =
    statementCardsByFramework(ctx, [slice.statements], slice.readModel).get(
      slice.framework,
    ) ?? []
  const stepThree = new Map(
    worklist
      .filter((i) => i.step === 3 && i.statementId)
      .map((i) => [i.statementId!, i]),
  )
  // The same supplier facts the open regulation's cards carry in the paged review:
  // the slice's pool, else its candidates (`/scope`'s own fallback).
  const supplierHasArticles =
    slice.poolNumbers.length > 0 || slice.candidateNumbers.length > 0
  for (const card of cards) {
    const issue = stepThree.get(card.id)
    card.openDetail = issue
      ? openPointDetail(issue, ctx, card.conditions)
      : null
    if (options.draftedStatements?.has(card.id)) card.draftQuestions = true
    card.supplierHasArticles = supplierHasArticles
    card.supplierMatched = slice.supplierMatched
  }
  return {
    framework: slice.framework,
    slice,
    ctx,
    issues: worklist.filter((i) => i.framework === slice.framework),
    cards,
    softenedCells: ctx.conditionFindingUnconfirmed ?? new Set<string>(),
    derivation: deriveFrameworkVerdict(
      cards.filter((c) => !c.excluded).map(toUnitFacts),
      options.inquiryStateOf?.(slice.framework) ?? "none",
    ),
  }
}

/**
 * `fetchQuery`, never `ensureQueryData` (which hands back stale cached data in
 * TanStack 5): with the default `staleTime` of 0 an invalidated or cached slice is
 * read again, so a gesture never acts on an article list older than the server's.
 */
export async function loadFrameworkState(
  queryClient: QueryClient,
  declId: string,
  framework: string,
  options: FrameworkStateOptions & {
    staleTime?: number
    /** The write counter the server's open items were read from. A slice read from
     *  an OLDER stored version is read again (fresh); still older, the load fails —
     *  never a gesture or a text from a state the server has moved past. */
    notOlderThan?: number | null
  } = {},
): Promise<FrameworkState> {
  const behind = (s: DeclarationFrameworkSlice) =>
    options.notOlderThan != null &&
    s.revision != null &&
    s.revision.writeSeq < options.notOlderThan
  let slice = await loadFrameworkSlice(queryClient, declId, framework, {
    staleTime: options.staleTime,
  })
  if (behind(slice)) {
    slice = await loadFrameworkSlice(queryClient, declId, framework)
    if (behind(slice)) throw new StaleFrameworkSliceError(framework)
  }
  return frameworkStateFromSlice(slice, options)
}

/** The regulation could only be read from a version older than the summary's. */
export class StaleFrameworkSliceError extends Error {
  constructor(readonly framework: string) {
    super(`The ${framework} slice is behind the open items`)
    this.name = "StaleFrameworkSliceError"
  }
}
